import AsyncSelect from '@/components/crud/AsyncSelect'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dropdown, DropdownItem } from '@/components/ui/dropdown'
import { Input } from '@/components/ui/input'
import { PageHeader } from '@/components/ui/page-header'
import { Select } from '@/components/ui/select'
import { SortableTH, Table, TBody, TD, THead, TR } from '@/components/ui/table'
import { useToast } from '@/components/ui/toast'
import api from '@/lib/api'
import { formatBRL, formatDate } from '@/lib/utils'
import { fetchReferenceList } from '@/services/referenceService'
import { CalendarClock, CheckCircle2, FileDown, Plus, Search, Wallet, X, XCircle } from 'lucide-react'
import { IoAlertCircleOutline } from "react-icons/io5"
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

const diasFiltros = [15, 30, 60, 90]

/** Cor do badge de "dias para aditivar" conforme urgência. */
function diasBadge(dias) {
  if (dias === null || dias === undefined) return null
  if (dias < 0) {
    const overdueDays = Math.abs(dias)
    return <Badge variant="destructive">Vencido há {overdueDays} {overdueDays === 1 ? 'dia' : 'dias'}</Badge>
  }
  let variant = 'muted'
  if (dias <= 15) variant = 'destructive'
  else if (dias <= 30) variant = 'warning'
  else if (dias <= 60) variant = 'default'
  return <Badge variant={variant}>{dias} dias</Badge>
}

/** Exibe o alerta visual da data conforme os dias restantes até o vencimento. */
function alertaData(data) {
  if (!data) return null

  const [year, month, day] = String(data).slice(0, 10).split('-').map(Number)
  const vencimento = new Date(year, month - 1, day)
  const hoje = new Date()
  const hojeSemHora = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate())
  const diasRestantes = Math.ceil((vencimento - hojeSemHora) / 86400000)

  let className = ''
  if (diasRestantes <= 15) className = 'text-red-600'
  else if (diasRestantes <= 30) className = 'text-orange-500'
  else if (diasRestantes <= 60) className = 'text-yellow-600'
  else return null

  const faixa = diasRestantes <= 15 ? 15 : diasRestantes <= 30 ? 30 : 60
  return (
    <IoAlertCircleOutline  
      className={`inline-block h-4 w-4 ml-1 align-[-2px] ${className}`}
      aria-label={`Vencimento em até ${faixa} dias`}
      title={`Vencimento em até ${faixa} dias`}
    />
  )
}

export default function ContratoList() {
  const navigate = useNavigate()
  const toast = useToast()
  const [items, setItems] = useState([])
  const [gerandoRelatorio, setGerandoRelatorio] = useState('')
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [sort, setSort] = useState({ key: 'aditivar', direction: 'asc' })

  const [q, setQ] = useState('')
  const [clienteID, setClienteID] = useState('')
  const [clienteLabel, setClienteLabel] = useState('')
  const [modalidadeID, setModalidadeID] = useState('')
  const [assinadoCliente, setAssinadoCliente] = useState('')
  // A listagem abre já filtrada pelos contratos ativos (padrão do dia a dia).
  const [ativo, setAtivo] = useState('1')
  const [dias, setDias] = useState('')
  const [modalidades, setModalidades] = useState([])

  const scrollRef = useRef(null)

  useEffect(() => {
    let cancelled = false

    fetchReferenceList('/tipos-compra', { limit: 100 })
      .then((items) => {
        if (cancelled) return
        setModalidades(items)
      })
      .catch(() => {
        if (cancelled) return
        setModalidades([])
        toast.error('Não foi possível carregar as modalidades.')
      })

    return () => {
      cancelled = true
    }
  }, [toast])

  const buildParams = useCallback(
    (nextOffset) => ({
      q,
      clienteID,
      modalidadeID,
      assinadoCliente,
      ativo,
      dias,
      sort: sort.key,
      direction: sort.direction,
      limit: 30,
      offset: nextOffset,
    }),
    [q, clienteID, modalidadeID, assinadoCliente, ativo, dias, sort]
  )

  const load = useCallback(
    async (reset) => {
      if (loading) return
      setLoading(true)
      try {
        const nextOffset = reset ? 0 : offset
        const { data } = await api.get('/contratos', { params: buildParams(nextOffset) })
        setItems((prev) => (reset ? data.data : [...prev, ...data.data]))
        setOffset(data.offset)
        setHasMore(data.hasMore)
      } finally {
        setLoading(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [buildParams, offset, loading]
  )

  // Recarrega ao alterar filtros (debounce da busca).
  useEffect(() => {
    const t = setTimeout(() => {
      setOffset(0)
      load(true)
    }, 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, clienteID, modalidadeID, assinadoCliente, ativo, dias, sort])

  function handleSort(key) {
    setSort((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  function onScroll(e) {
    const el = e.target
    if (hasMore && !loading && el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
      load(false)
    }
  }

  // "Limpar" volta ao estado inicial da tela, que já traz apenas os contratos ativos.
  const hasFilters = q || clienteID || modalidadeID || assinadoCliente !== '' || ativo !== '1' || dias
  function clearFilters() {
    setQ(''); setClienteID(''); setClienteLabel(''); setModalidadeID('')
    setAssinadoCliente(''); setAtivo('1'); setDias('')
  }

  // O PDF vem pela API autenticada (o token vai no header), por isso baixamos o
  // blob e só então abrimos a nova aba.
  async function abrirRelatorio(tipo) {
    if (gerandoRelatorio) return
    setGerandoRelatorio(tipo)
    try {
      const { data, headers } = await api.get('/contratos/relatorio', {
        params: { tipo },
        responseType: 'blob',
      })
      const url = URL.createObjectURL(new Blob([data], { type: headers['content-type'] || 'application/pdf' }))
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch {
      toast.error('Não foi possível gerar o relatório.')
    } finally {
      setGerandoRelatorio('')
    }
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Contratos"
        subtitle="Compras, contratos e aditivos."
        actions={
          <>
            <Dropdown
              align="right"
              trigger={
                <Button variant="outline" disabled={!!gerandoRelatorio}>
                  <FileDown className="h-4 w-4" /> {gerandoRelatorio ? 'Gerando...' : 'Relatórios'}
                </Button>
              }
            >
              <DropdownItem onClick={() => abrirRelatorio('faturamentos')}>
                <Wallet className="h-4 w-4" />
                Faturamento
                </DropdownItem>
              <DropdownItem disabled className="pointer-events-none opacity-50" onClick={() => abrirRelatorio('renovacoes')}>
                <CalendarClock className="h-4 w-4" />
                Renovações de contrato
                </DropdownItem>
            </Dropdown>
            <Button onClick={() => navigate('/contrato/novo')}><Plus className="h-4 w-4" /> Novo</Button>
          </>
        }
      >
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar contrato, cliente, processo..." className="pl-9" />
          </div>

          <div className="w-56">
            <AsyncSelect
              endpoint="/clientes"
              value={clienteID}
              onChange={(v) => setClienteID(v)}
              optionValue="clienteID"
              optionLabel="nome"
              placeholder="Cliente"
              initialLabel={clienteLabel}
            />
          </div>

          <Select value={modalidadeID} onChange={(e) => setModalidadeID(e.target.value)} className="w-44">
            <option value="">Modalidade</option>
            {modalidades.map((m) => (
              <option key={m.modalidadeID} value={m.modalidadeID}>{m.nome}</option>
            ))}
          </Select>

          <Select value={assinadoCliente} onChange={(e) => setAssinadoCliente(e.target.value)} className="w-40">
            <option value="">Ass. cliente: todos</option>
            <option value="1">Assinado</option>
            <option value="0">Não assinado</option>
          </Select>

          <Select value={ativo} onChange={(e) => setAtivo(e.target.value)} className="w-36">
            <option value="">Status: todos</option>
            <option value="1">Ativos</option>
            <option value="0">Inativos</option>
          </Select>

          <div className="flex items-center gap-1">
            {diasFiltros.map((d) => (
              <button
                key={d}
                onClick={() => setDias((cur) => (String(cur) === String(d) ? '' : String(d)))}
                className={
                  'px-2.5 h-9 rounded-md text-sm border transition-colors ' +
                  (String(dias) === String(d)
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'border-border hover:bg-accent/60')
                }
                title={`Aditivar em até ${d} dias`}
              >
                {d}d
              </button>
            ))}
          </div>

          {hasFilters && (
            <Button variant="outline" size="sm" onClick={clearFilters}>
              <X className="h-4 w-4" /> Limpar
            </Button>
          )}
        </div>
      </PageHeader>

      <div ref={scrollRef} onScroll={onScroll} className="flex-1 overflow-auto p-6 pt-4">
        <Table>
          <THead>
            <tr>
              <SortableTH sortKey="id" activeSortKey={sort.key} direction={sort.direction} onSort={handleSort}>ID</SortableTH>
              <SortableTH sortKey="cliente" activeSortKey={sort.key} direction={sort.direction} onSort={handleSort}>Cliente</SortableTH>
              <SortableTH sortKey="numeroContrato" activeSortKey={sort.key} direction={sort.direction} onSort={handleSort}>Nº Contrato</SortableTH>
              <SortableTH sortKey="modalidade" activeSortKey={sort.key} direction={sort.direction} onSort={handleSort}>Modalidade</SortableTH>
              <SortableTH sortKey="dataFimCompra" activeSortKey={sort.key} direction={sort.direction} onSort={handleSort}>Fim compra</SortableTH>
              <SortableTH sortKey="dataFimServicoValor" activeSortKey={sort.key} direction={sort.direction} onSort={handleSort}>Fim serviço/valor</SortableTH>
              <SortableTH sortKey="dataFimAditivoPrazo" activeSortKey={sort.key} direction={sort.direction} onSort={handleSort}>Fim prazo</SortableTH>
              <SortableTH sortKey="aditivar" activeSortKey={sort.key} direction={sort.direction} onSort={handleSort}>Aditivar</SortableTH>
              <SortableTH sortKey="valor" activeSortKey={sort.key} direction={sort.direction} onSort={handleSort} align="right">Valor</SortableTH>
              <SortableTH sortKey="assinado" activeSortKey={sort.key} direction={sort.direction} onSort={handleSort} align="center">Ass.</SortableTH>
              <SortableTH sortKey="status" activeSortKey={sort.key} direction={sort.direction} onSort={handleSort} align="center">Status</SortableTH>
            </tr>
          </THead>
          <TBody>
            {items.map((c, i) => (
              <TR key={c.contratoID} index={i} clickable onClick={() => navigate(`/contrato/${c.contratoID}`)}>
                <TD>{c.contratoID}</TD>
                <TD>
                  <div className="font-medium">{c.clienteNome}</div>
                  <div className="text-xs text-muted-foreground">{c.clienteCnpj}</div>
                </TD>
                <TD>{c.numeroContrato}</TD>
                <TD>{c.modalidadeNome}</TD>
                <TD>{formatDate(c.dataFimCompra)}{alertaData(c.dataFimCompra)}</TD>
                <TD>{c.dataFimServicoValor ? <>{formatDate(c.dataFimServicoValor)}{alertaData(c.dataFimServicoValor)}</> : <span className="text-muted-foreground">—</span>}</TD>
                <TD>{c.dataFimAditivoPrazo ? <>{formatDate(c.dataFimAditivoPrazo)}{alertaData(c.dataFimAditivoPrazo)}</> : <span className="text-muted-foreground">—</span>}</TD>
                <TD>{diasBadge(c.diasParaAditivar)}</TD>
                <TD className="text-right">{formatBRL(c.valorTotal)}</TD>
                <TD className="text-center">
                  {c.assinadoCliente ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 inline" />
                  ) : (
                    <XCircle className="h-4 w-4 text-muted-foreground inline" />
                  )}
                </TD>
                <TD className="text-center">
                  {c.ativo ? <Badge variant="success">Ativo</Badge> : <Badge variant="muted">Inativo</Badge>}
                </TD>
              </TR>
            ))}
            {items.length === 0 && !loading && (
              <tr>
                <TD colSpan={11} className="text-center text-muted-foreground py-10">Nenhum contrato encontrado.</TD>
              </tr>
            )}
          </TBody>
        </Table>
        {loading && <p className="text-center text-sm text-muted-foreground py-4">Carregando...</p>}
      </div>
    </div>
  )
}
